import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PillTabs, PageSubtitle } from "../components/lux";
import LeaderboardBoard from "../components/affiliates/LeaderboardBoard";
import ChallengeCard from "../components/affiliates/ChallengeCard";
import { Plus, Trash, PencilSimple, Trophy, UsersThree, Target, FloppyDisk, X } from "@phosphor-icons/react";

const TABS = [
  { key: "leaderboard", label: "Leaderboard" },
  { key: "roster", label: "Affiliates" },
  { key: "challenges", label: "Challenges" },
];

const METRICS = [
  { key: "bookings", label: "Bookings brought in" },
  { key: "rental_days", label: "Rental days brought in" },
  { key: "manual", label: "Custom metric (you enter the scores)" },
];

const emptyChallenge = {
  title: "", prize: "", description: "", metric: "bookings", metric_label: "",
  target: 5, start_date: "", end_date: "", vehicle_class: "any", vehicle_id: "any",
  booking_type: "any", active: true,
};

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/** INS-017: an insurance cut is either dollars or a share of the rental total.
 *  Switching CLEARS the number — $150 must never quietly become 150%. */
const CUT_TYPES = [
  { key: "amount", symbol: "$", label: "A fixed amount in dollars" },
  { key: "percent", symbol: "%", label: "A percentage of the rental total" },
];

const CutTypeToggle = ({ value, onChange, testidPrefix, describedBy }) => (
  <div role="group" aria-label="Insurance cut type" aria-describedby={describedBy}
    className="inline-flex shrink-0 overflow-hidden rounded-md border border-border">
    {CUT_TYPES.map((t) => (
      <button
        key={t.key} type="button" aria-pressed={value === t.key} aria-label={t.label} title={t.label}
        onClick={() => value !== t.key && onChange(t.key)}
        className={`h-9 w-9 text-sm transition-colors ${value === t.key
          ? "bg-primary text-primary-foreground"
          : "bg-input text-muted-foreground hover:text-foreground"}`}
        data-testid={`${testidPrefix}-${t.key}`}
      >
        {t.symbol}
      </button>
    ))}
  </div>
);

const cutText = (payouts) => {
  const amount = Number(payouts?.insurance) || 0;
  return payouts?.insurance_type === "percent" ? `${amount}%` : `$${amount.toFixed(2)}`;
};

export default function AffiliateManagement() {
  const [tab, setTab] = useState("leaderboard");
  const [month, setMonth] = useState(monthKey(new Date()));
  const [board, setBoard] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [newName, setNewName] = useState("");
  const [newCut, setNewCut] = useState("");
  const [newCutType, setNewCutType] = useState("amount");
  const [editing, setEditing] = useState(null);        // { id, name, active, cut, cutType }
  const [chOpen, setChOpen] = useState(false);
  const [chForm, setChForm] = useState(emptyChallenge);
  const [chEditId, setChEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [b, a, c] = await Promise.all([
      api.get(`/affiliates/leaderboard?month=${month}`),
      api.get("/affiliates"),
      api.get("/affiliates/challenges"),
    ]);
    setBoard(b.data);
    setAffiliates(a.data || []);
    setChallenges(c.data || []);
  };

  useEffect(() => { load().catch(() => toast.error("Could not load affiliate data")); /* eslint-disable-next-line */ }, [month]);
  useEffect(() => { api.get("/vehicles").then(({ data }) => setVehicles(data || [])).catch(() => {}); }, []);

  const classes = useMemo(
    () => [...new Set(vehicles.map((v) => (v.vehicle_class || "").trim()).filter(Boolean))].sort(),
    [vehicles]
  );

  const addAffiliate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post("/affiliates", {
        name, active: true,
        default_payouts: { insurance: Number(newCut) || 0, insurance_type: newCutType },
      });
      setNewName("");
      setNewCut("");
      setNewCutType("amount");
      toast.success(`${name} added`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add affiliate"); }
  };

  const saveName = async () => {
    try {
      await api.put(`/affiliates/${editing.id}`, {
        name: editing.name.trim(), active: editing.active,
        default_payouts: { insurance: Number(editing.cut) || 0, insurance_type: editing.cutType || "amount" },
      });
      setEditing(null);
      toast.success("Affiliate updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update affiliate"); }
  };

  const toggleActive = async (a) => {
    try {
      await api.put(`/affiliates/${a.id}`, { name: a.name, active: !a.active });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update affiliate"); }
  };

  const removeAffiliate = async (a) => {
    if (!window.confirm(`Remove ${a.name}? Their past bookings keep the credit.`)) return;
    await api.delete(`/affiliates/${a.id}`);
    toast.success(`${a.name} removed`);
    load();
  };

  const openChallenge = (ch) => {
    if (ch) {
      setChEditId(ch.id);
      setChForm({
        ...emptyChallenge, ...ch,
        vehicle_class: ch.vehicle_class || "any",
        vehicle_id: ch.vehicle_id || "any",
        booking_type: ch.booking_type || "any",
      });
    } else {
      setChEditId(null);
      setChForm(emptyChallenge);
    }
    setChOpen(true);
  };

  const saveChallenge = async () => {
    if (!chForm.title.trim()) { toast.error("Give the challenge a title"); return; }
    setSaving(true);
    const body = {
      ...chForm,
      title: chForm.title.trim(),
      target: Number(chForm.target) || 0,
      vehicle_class: chForm.vehicle_class === "any" ? "" : chForm.vehicle_class,
      vehicle_id: chForm.vehicle_id === "any" ? "" : chForm.vehicle_id,
      booking_type: chForm.booking_type === "any" ? "" : chForm.booking_type,
    };
    try {
      if (chEditId) await api.put(`/affiliates/challenges/${chEditId}`, body);
      else await api.post("/affiliates/challenges", body);
      setChOpen(false);
      toast.success(chEditId ? "Challenge updated" : "Challenge created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save challenge"); }
    finally { setSaving(false); }
  };

  const removeChallenge = async (ch) => {
    if (!window.confirm(`Delete "${ch.title}"?`)) return;
    await api.delete(`/affiliates/challenges/${ch.id}`);
    toast.success("Challenge deleted");
    load();
  };

  const saveManual = async (ch, progress) => {
    await api.put(`/affiliates/challenges/${ch.id}/progress`, { progress });
    toast.success("Scores saved");
    load();
  };

  return (
    <div className="space-y-8" data-testid="affiliate-management-page">
      <div>
        <PageSubtitle>Who brings the business in — roster, monthly leaderboard and challenges</PageSubtitle>
        <PillTabs tabs={TABS} value={tab} onChange={setTab} testid="affiliate-tab" />
      </div>

      {tab === "leaderboard" && (
        <LeaderboardBoard board={board} month={month} onMonth={setMonth} testid="admin-leaderboard" />
      )}

      {tab === "roster" && (
        <div className="border border-border bg-card p-6" data-testid="affiliate-roster">
          <div className="flex items-center gap-2 mb-1">
            <UsersThree size={16} className="text-primary" />
            <h2 className="font-heading text-2xl">Affiliates</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-5">These names fill the "Which affiliate?" dropdown on the booking form.</p>

          <div className="flex flex-wrap items-end gap-2 mb-6">
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="new-affiliate-name-input" className="label-mono mb-1.5 block">Affiliate name</Label>
              <Input
                id="new-affiliate-name-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addAffiliate()}
                placeholder="Affiliate name"
                className="bg-input border-border"
                data-testid="new-affiliate-name"
              />
            </div>
            <div className="w-52">
              <Label htmlFor="new-affiliate-cut-input" className="label-mono mb-1.5 block whitespace-nowrap">
                Insurance cut ({newCutType === "percent" ? "%" : "$"})
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="new-affiliate-cut-input"
                  type="number" min="0" max={newCutType === "percent" ? "100" : undefined}
                  step={newCutType === "percent" ? "0.1" : "0.01"}
                  value={newCut}
                  onChange={(e) => setNewCut(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addAffiliate()}
                  placeholder={newCutType === "percent" ? "0" : "0.00"}
                  aria-describedby="new-affiliate-cut-hint"
                  className="bg-input border-border money"
                  data-testid="new-affiliate-cut"
                />
                <CutTypeToggle
                  value={newCutType}
                  onChange={(kind) => { setNewCutType(kind); setNewCut(""); }}
                  testidPrefix="new-affiliate-cut-type"
                  describedBy="new-affiliate-cut-hint"
                />
              </div>
              <p id="new-affiliate-cut-hint" className="sr-only">
                Their standing cut on an insurance replacement claim. A percentage is taken from the
                pre-tax rental total. Switching between dollars and percent clears the number.
              </p>
            </div>
            <Button onClick={addAffiliate} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-affiliate-button">
              <Plus size={14} className="mr-1.5" />Add
            </Button>
          </div>

          {affiliates.length === 0 ? (
            <p className="text-sm text-muted-foreground">No affiliates yet.</p>
          ) : (
            <ul className="divide-y divide-border border border-border" data-testid="affiliate-list">
              {affiliates.map((a) => (
                <li key={a.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`affiliate-row-${a.id}`}>
                  {editing?.id === a.id ? (
                    <div className="flex flex-wrap items-center gap-2 flex-1">
                      {/* Labels are real and associated, just visually hidden —
                          the row has no space for them inline. */}
                      <Label htmlFor={`affiliate-edit-name-${a.id}`} className="sr-only">
                        Affiliate name for {a.name}
                      </Label>
                      <Input
                        id={`affiliate-edit-name-${a.id}`}
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        className="bg-input border-border h-9 max-w-xs"
                        data-testid={`affiliate-edit-input-${a.id}`}
                      />
                      <Label htmlFor={`affiliate-edit-cut-input-${a.id}`} className="sr-only">
                        Insurance cut ({editing.cutType === "percent" ? "%" : "$"}) for {a.name}
                      </Label>
                      <Input
                        id={`affiliate-edit-cut-input-${a.id}`}
                        type="number" min="0" max={editing.cutType === "percent" ? "100" : undefined}
                        step={editing.cutType === "percent" ? "0.1" : "0.01"}
                        value={editing.cut}
                        onChange={(e) => setEditing({ ...editing, cut: e.target.value })}
                        placeholder={editing.cutType === "percent" ? "Insurance cut (%)" : "Insurance cut ($)"}
                        className="bg-input border-border h-9 w-44 money"
                        data-testid={`affiliate-edit-cut-${a.id}`}
                      />
                      <CutTypeToggle
                        value={editing.cutType === "percent" ? "percent" : "amount"}
                        onChange={(kind) => setEditing({ ...editing, cutType: kind, cut: "" })}
                        testidPrefix={`affiliate-edit-cut-type-${a.id}`}
                      />
                      <Button size="sm" onClick={saveName} data-testid={`affiliate-save-${a.id}`}><FloppyDisk size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}><X size={14} /></Button>
                    </div>
                  ) : (
                    <div className="min-w-0">
                      <div className="font-heading truncate">{a.name}</div>
                      <div className="text-xs text-muted-foreground" data-testid={`affiliate-cut-${a.id}`}>
                        Insurance replacement cut{" "}
                        <span className="money text-foreground">
                          {cutText(a.default_payouts)}
                        </span>
                      </div>
                      <button
                        onClick={() => toggleActive(a)}
                        className={`label-mono text-[10px] ${a.active ? "text-emerald-500" : "text-muted-foreground"}`}
                        data-testid={`affiliate-toggle-${a.id}`}
                      >
                        {a.active ? "● Active — shows in the dropdown" : "○ Inactive — hidden from the dropdown"}
                      </button>
                    </div>
                  )}
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => setEditing({ id: a.id, name: a.name, active: a.active, cut: String(a.default_payouts?.insurance ?? 0), cutType: a.default_payouts?.insurance_type === "percent" ? "percent" : "amount" })} data-testid={`affiliate-edit-${a.id}`}>
                      <PencilSimple size={14} className="text-primary" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeAffiliate(a)} data-testid={`affiliate-delete-${a.id}`}>
                      <Trash size={14} className="text-red-400" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "challenges" && (
        <div className="space-y-4" data-testid="affiliate-challenges-admin">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Target size={16} className="text-primary" />
              <h2 className="font-heading text-2xl">Challenges</h2>
            </div>
            <Button onClick={() => openChallenge(null)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="new-challenge-button">
              <Plus size={14} className="mr-1.5" />New challenge
            </Button>
          </div>

          {challenges.length === 0 ? (
            <div className="border border-border bg-card p-8 text-center">
              <Trophy size={22} className="text-primary mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No challenges yet — create one and it appears on every affiliate&apos;s leaderboard tab.</p>
            </div>
          ) : (
            challenges.map((ch) => (
              <ChallengeCard
                key={ch.id}
                challenge={ch}
                admin
                onEdit={() => openChallenge(ch)}
                onDelete={() => removeChallenge(ch)}
                onSaveManual={(progress) => saveManual(ch, progress)}
              />
            ))
          )}
        </div>
      )}

      <Dialog open={chOpen} onOpenChange={setChOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="challenge-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{chEditId ? "Edit challenge" : "New challenge"}</DialogTitle>
            <DialogDescription>Set what to chase, the target and the prize — affiliates see their progress and rank live.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="label-mono mb-1.5 block">Title *</Label>
                <Input value={chForm.title} onChange={(e) => setChForm({ ...chForm, title: e.target.value })} placeholder="June SUV sprint" className="bg-input border-border" data-testid="challenge-title" />
              </div>
              <div>
                <Label className="label-mono mb-1.5 block">Prize / reward</Label>
                <Input value={chForm.prize} onChange={(e) => setChForm({ ...chForm, prize: e.target.value })} placeholder="$500 bonus" className="bg-input border-border" data-testid="challenge-prize" />
              </div>
            </div>

            <div>
              <Label className="label-mono mb-1.5 block">Details</Label>
              <Textarea rows={2} value={chForm.description} onChange={(e) => setChForm({ ...chForm, description: e.target.value })} placeholder="Anything the affiliates should know" className="bg-input border-border" data-testid="challenge-description" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="label-mono mb-1.5 block">What counts</Label>
                <Select value={chForm.metric} onValueChange={(v) => setChForm({ ...chForm, metric: v })}>
                  <SelectTrigger className="bg-input border-border" data-testid="challenge-metric"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METRICS.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-mono mb-1.5 block">Target</Label>
                <Input type="number" value={chForm.target} onChange={(e) => setChForm({ ...chForm, target: e.target.value })} className="bg-input border-border" data-testid="challenge-target" />
              </div>
              {chForm.metric === "manual" && (
                <div>
                  <Label className="label-mono mb-1.5 block">Unit label</Label>
                  <Input value={chForm.metric_label} onChange={(e) => setChForm({ ...chForm, metric_label: e.target.value })} placeholder="referrals, posts…" className="bg-input border-border" data-testid="challenge-metric-label" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="label-mono mb-1.5 block">Starts</Label>
                <Input type="date" value={chForm.start_date} onChange={(e) => setChForm({ ...chForm, start_date: e.target.value })} className="bg-input border-border" data-testid="challenge-start" />
              </div>
              <div>
                <Label className="label-mono mb-1.5 block">Ends</Label>
                <Input type="date" value={chForm.end_date} onChange={(e) => setChForm({ ...chForm, end_date: e.target.value })} className="bg-input border-border" data-testid="challenge-end" />
              </div>
            </div>

            {chForm.metric !== "manual" && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="label-mono mb-1.5 block">Only this class</Label>
                  <Select value={chForm.vehicle_class} onValueChange={(v) => setChForm({ ...chForm, vehicle_class: v })}>
                    <SelectTrigger className="bg-input border-border" data-testid="challenge-class"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any class</SelectItem>
                      {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono mb-1.5 block">Only this car</Label>
                  <Select value={chForm.vehicle_id} onValueChange={(v) => setChForm({ ...chForm, vehicle_id: v })}>
                    <SelectTrigger className="bg-input border-border" data-testid="challenge-vehicle"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="any">Any car</SelectItem>
                      {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.make} {v.model}{v.license_plate ? ` · ${v.license_plate}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono mb-1.5 block">Booking type</Label>
                  <Select value={chForm.booking_type} onValueChange={(v) => setChForm({ ...chForm, booking_type: v })}>
                    <SelectTrigger className="bg-input border-border" data-testid="challenge-booking-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="rental">Rental</SelectItem>
                      <SelectItem value="chauffeur">Chauffeur</SelectItem>
                      <SelectItem value="tour">Tour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={chForm.active} onChange={() => setChForm({ ...chForm, active: !chForm.active })} data-testid="challenge-active" />
              Live — show this challenge to affiliates
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setChOpen(false)}>Cancel</Button>
            <Button onClick={saveChallenge} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-challenge-button">
              {saving ? "Saving…" : chEditId ? "Save changes" : "Create challenge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
